import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { Heart, Users, Shield, Activity, FileText, Award } from "lucide-react";
import { getImageUrl } from '@/lib/utils';

const EmployeeBenefits = () => {
  const pageData = {
    // SEO Meta Data
    title: "Employee Benefits Insurance UAE | Medical & Life Coverage | Yalla Compare",
    metaDescription: "Comprehensive employee benefits insurance in UAE. Medical insurance, life coverage, and workers compensation. Attract top talent with competitive benefits packages.",

    // Hero Section
    heroTitle: "Comprehensive Employee Benefits for UAE Businesses",
    heroSubtitle: "Employee Benefits",
    heroDescription: "Attract and retain top talent with comprehensive employee benefits packages. From medical insurance to life coverage, provide the protection your team deserves.",
    heroImage: getImageUrl("eb-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Comprehensive medical insurance for all employees",
      "Life and disability coverage options",
      "Workers compensation as required by UAE law",
      "Flexible benefit packages to suit different needs",
      "Group rates for cost-effective coverage",
      "Easy enrollment and claims management"
    ],

    // Features Section
    features: [
      {
        title: "Medical Insurance",
        description: "Comprehensive healthcare coverage including inpatient, outpatient, and emergency medical treatment.",
        icon: <Heart className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Life Insurance",
        description: "Financial protection for employees' families with term life and accidental death coverage options.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Workers Compensation",
        description: "Mandatory coverage for workplace injuries and occupational diseases as required by UAE labor law.",
        icon: <Users className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Wellness Programs",
        description: "Preventive care and wellness initiatives to keep your workforce healthy and productive.",
        icon: <Activity className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Compliance Support",
        description: "Ensure full compliance with UAE employment and insurance regulations for all benefit programs.",
        icon: <FileText className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Employee Satisfaction",
        description: "Boost employee morale and retention with competitive benefits that demonstrate your commitment.",
        icon: <Award className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "Medical Insurance Coverage",
        description: "Comprehensive healthcare protection for your employees",
        included: [
          "Inpatient hospital treatment",
          "Outpatient consultations and diagnostics",
          "Emergency medical care",
          "Prescription medications",
          "Specialist consultations",
          "Dental and optical coverage",
          "Maternity and newborn care",
          "Preventive health screenings"
        ]
      },
      {
        title: "Additional Benefits",
        description: "Extended protection and support services",
        included: [
          "Life insurance coverage",
          "Accidental death and dismemberment",
          "Temporary total disability",
          "Permanent partial disability",
          "Medical evacuation services",
          "Repatriation coverage",
          "Critical illness protection",
          "Employee assistance programs"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "UAE Compliance",
        description: "Expert knowledge of UAE labor law requirements and mandatory insurance obligations."
      },
      {
        title: "Flexible Plans",
        description: "Customizable benefit packages that fit your budget and employee needs across different levels."
      },
      {
        title: "Group Advantages",
        description: "Leverage group buying power for better rates and enhanced coverage options for your team."
      },
      {
        title: "Easy Management",
        description: "Streamlined administration with digital platforms for enrollment, claims, and policy management."
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "Is medical insurance mandatory for employees in the UAE?",
        answer: "Yes, UAE law requires employers to provide medical insurance for all employees. The coverage must meet minimum standards set by local health authorities."
      },
      {
        question: "What is the minimum medical insurance coverage required?",
        answer: "The minimum coverage varies by emirate but typically includes inpatient care up to AED 150,000 annually, outpatient care, emergency treatment, and maternity coverage."
      },
      {
        question: "Can employees add family members to the medical insurance?",
        answer: "Yes, most policies allow employees to add spouses and children to their coverage, either at company expense or through employee contributions."
      },
      {
        question: "How does workers compensation work in the UAE?",
        answer: "Workers compensation provides medical treatment and wage replacement for employees injured at work. It's mandatory and covers medical expenses, disability benefits, and death benefits."
      }
    ],

    // CTA Section
    ctaTitle: "Invest in Your Team's Well-being",
    ctaDescription: "Provide comprehensive employee benefits that attract talent, ensure compliance, and demonstrate your commitment to your workforce.",
    primaryCta: "Get Employee Benefits Quote",
    secondaryCta: "Compare Benefit Options",

    // Product ID for quote form
    productId: "employee-medical"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default EmployeeBenefits;
